import { ExtendRepoObjects } from "./createJob";
import {
  ExtendedRepositoryObject,
  RepositoryObject,
  RepositoryTree
} from "../../../backend/types";
import { getFlattenedTree } from "../../../helpers/treeHelper";

/**
 * Retrieves the repository object for execution based on the provided execution file source.
 *
 * @param {string} executionFileSource - The source of the execution file, either "phil" or another environment.
 * @param {Function} callRepositoryObjects - Function to get file versions from the repository.
 * @param {GetRepoObjectQueryData[]} repos - Array of repository objects from PhIL or IE files.
 * @param {ExtendRepoObjects} executionRepoObject - The execution repository object containing repository and version details.
 */

export const getRepoObjectForExecution = async (
  executionFileSource: string,
  callRepositoryObjects: Function,
  callSingleRepositoryObject: Function,
  executionRepoObject: ExtendRepoObjects,
  repos?: RepositoryTree[] // only tree from IE is possibble due to changes on the repository object query
) => {
  switch (executionFileSource) {
    case "phil": {
      const { data: singleRepositoryObject } = await callSingleRepositoryObject(
        {
          variables: {
            versionId: executionRepoObject.versionId
          }
        }
      );
      const objectName =
        singleRepositoryObject.getRepositoryObject.repositoryObject.name;
      const segments = objectName.split("/");
      const name = segments.length > 1 ? segments.pop()! : objectName;
      let location = segments.length > 0 ? segments.join("/") : "/";
      if (location === name || location === "") {
        location = "/";
      }
      const { data: repositoryObjectVersions } = await callRepositoryObjects({
        variables: {
          repoId: Number(executionRepoObject.repositoryId),
          location,
          name,
          versioned: true
        }
      });
      const file =
        repositoryObjectVersions.getRepositoryObjects.repositoryObjects[0];

      return {
        ...executionRepoObject,
        repositoryId: executionRepoObject.repositoryId,
        versionId: file?.versionId ?? executionRepoObject.versionId,
        absolutePath: `${file.repository.name}/${file.name}`,
        notFound: file?.deletedAt ? true : false
      };
    }
    default: {
      // when environment is not phil
      // we always get file version for IE to retrieve name of file
      const flattenRepoObjects = getFlattenedTree(repos!);

      if (
        executionRepoObject.versionId === "" ||
        executionRepoObject.versionId === null
      ) {
        const foundFile = flattenRepoObjects.find(
          (file: any) =>
            `${file.repository.name}/${file.absolutePath}` ===
            executionRepoObject.absolutePath
        );

        return {
          repositoryId: executionRepoObject.repositoryId,
          versionId: foundFile?.versionId || "",
          absolutePath: executionRepoObject.absolutePath,
          notFound: foundFile ? false : true
        };
      } else {
        const { data: singleRepositoryObject } =
          await callSingleRepositoryObject({
            variables: {
              versionId: executionRepoObject.versionId
            }
          });
        const object =
          singleRepositoryObject.getRepositoryObject.repositoryObject;

        const foundFile = flattenRepoObjects.find(
          (repositoryObject: ExtendedRepositoryObject) =>
            repositoryObject.repository.id === object.repository.id &&
            repositoryObject.absolutePath === object.name
        );

        return {
          ...executionRepoObject,
          repositoryId: executionRepoObject.repositoryId,
          versionId: executionRepoObject.versionId,
          absolutePath: `${object.repository.name}/${object.name}`,
          notFound: foundFile ? false : true
        };
      }
    }
  }
};

export const getFileNameAndRepoName = (file: any) => {
  const paths = file.split("/");
  const repoName = paths[0];
  const fileName = paths.slice(1).join("/");

  return [repoName, fileName];
};
